import { useState, useEffect } from 'react';
import { adminFunctions } from '../../firebase/adminFunctions';
import {
  Table,
  Tbody,
  Thead,
  Tr,
  Th,
  Td,
  Button,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Select,
  IconButton,
  Box,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Input,
} from '@chakra-ui/react';
import { Pencil, Trash, Plus } from '@phosphor-icons/react';

export default function AutoDetectMappingManager() {
  const [mappings, setMappings] = useState({});
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [editMapping, setEditMapping] = useState(null);
  const [newMapping, setNewMapping] = useState({ roboflowClass: '', featureId: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const toast = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mappingsData, featuresData] = await Promise.all([
        adminFunctions.getAutoDetectMappings(),
        adminFunctions.getAllFeatures()
      ]);
      setMappings(mappingsData);
      setFeatures(featuresData);
    } catch (error) {
      toast({
        title: 'Error loading data',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMapping = async () => {
    try {
      await adminFunctions.updateAutoDetectMapping(
        editMapping.roboflowClass,
        editMapping.featureId
      );
      await loadData();
      setEditMapping(null);
      toast({
        title: 'Mapping updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating mapping',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCreateMapping = async () => {
    try {
      await adminFunctions.createAutoDetectMapping(
        newMapping.roboflowClass,
        newMapping.featureId
      );
      await loadData();
      setShowAddModal(false);
      setNewMapping({ roboflowClass: '', featureId: '' });
      toast({
        title: 'Mapping created',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error creating mapping',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteMapping = async (roboflowClass) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) {
      return;
    }

    try {
      await adminFunctions.deleteAutoDetectMapping(roboflowClass);
      await loadData();
      toast({
        title: 'Mapping deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error deleting mapping',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner />
      </Box>
    );
  }

  const getFeatureLabel = (featureId) => {
    return features[featureId]?.label || 'Unknown Feature';
  };

  return (
    <Box>
      <VStack spacing={4} align="stretch">
        <HStack justify="space-between">
          <Text fontSize="lg" fontWeight="medium">Auto-detect Mappings</Text>
          <Button
            leftIcon={<Plus size={16} />}
            colorScheme="blue"
            onClick={() => setShowAddModal(true)}
          >
            Add Mapping
          </Button>
        </HStack>

        <Alert status="info">
          <AlertIcon />
          Map Roboflow detection classes to features. When images are analyzed,
          detected objects will automatically enable the mapped features for the site.
        </Alert>

        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Roboflow Class</Th>
              <Th>Mapped Feature</Th>
              <Th width="100px">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {Object.entries(mappings).map(([roboflowClass, featureId]) => (
              <Tr key={roboflowClass}>
                <Td>{roboflowClass}</Td>
                <Td>{getFeatureLabel(featureId)}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<Pencil size={16} />}
                      size="sm"
                      aria-label="Edit mapping"
                      onClick={() => setEditMapping({ roboflowClass, featureId })}
                    />
                    <IconButton
                      icon={<Trash size={16} />}
                      size="sm"
                      colorScheme="red"
                      aria-label="Delete mapping"
                      onClick={() => handleDeleteMapping(roboflowClass)}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {/* Edit Modal */}
        <Modal
          isOpen={!!editMapping}
          onClose={() => setEditMapping(null)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Mapping</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={4}>
                <Text>Roboflow Class: {editMapping?.roboflowClass}</Text>

                <Select
                  value={editMapping?.featureId}
                  onChange={(e) => setEditMapping({
                    ...editMapping,
                    featureId: e.target.value
                  })}
                >
                  {Object.entries(features).map(([id, feature]) => (
                    <option key={id} value={id}>
                      {feature.label}
                    </option>
                  ))}
                </Select>

                <HStack justify="flex-end" width="100%">
                  <Button variant="ghost" onClick={() => setEditMapping(null)}>
                    Cancel
                  </Button>
                  <Button colorScheme="blue" onClick={handleUpdateMapping}>
                    Save Changes
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Add Modal */}
        <Modal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setNewMapping({ roboflowClass: '', featureId: '' });
          }}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Add New Mapping</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={4}>
                <Input
                  placeholder="Roboflow class name"
                  value={newMapping.roboflowClass}
                  onChange={(e) => setNewMapping({
                    ...newMapping,
                    roboflowClass: e.target.value
                  })}
                />

                <Select
                  placeholder="Select feature"
                  value={newMapping.featureId}
                  onChange={(e) => setNewMapping({
                    ...newMapping,
                    featureId: e.target.value
                  })}
                >
                  {Object.entries(features).map(([id, feature]) => (
                    <option key={id} value={id}>
                      {feature.label}
                    </option>
                  ))}
                </Select>

                <HStack justify="flex-end" width="100%">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowAddModal(false);
                      setNewMapping({ roboflowClass: '', featureId: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={handleCreateMapping}
                    isDisabled={!newMapping.roboflowClass || !newMapping.featureId}
                  >
                    Create Mapping
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
}
